import { useEffect, useMemo, useRef, useState } from 'react';
import { SelectControl } from '@gsa/afp-shared-form-utils';
import { Spinner, Alert } from '@gsa/afp-component-library';
import { useGetStateList } from '../../../../../requests/vehicle-referral';

const USA_TERRITORIES = ['US', 'PR', 'VI', 'GU', 'MP', 'AS', 'FM', 'MH', 'PW'];

export function isInternationalCountry(country) {
  return country && !USA_TERRITORIES.includes(country.toUpperCase().trim());
}

export default function generateAdditionalsGarageFormContent({
  defaultValues,
  countries,
  defaultStates,
  sinAlert,
}) {
  const [getStates, { loading: statesLoading }] = useGetStateList();

  const values = useMemo(
    () => ({
      provideReason: '-1',
      acknowledge: false,
      countryCode: 'US',
      addressLine1: '',
      addressLine2: '',
      city: '',
      stateCode: '-1',
      zipCode: '',
      usedForTowing: 'no',
      towingWeight: null,
      towingWeightUnit: 'lbs',
      ...defaultValues,
    }),
    [],
  );

  const selectOptions = [
    { label: '-Select-', value: '-1' },
    { label: 'Expansion of mission', value: 'Expansion of mission' },
    {
      label: 'Replace agency-owned vehicle',
      value: 'Replace agency-owned vehicle',
    },
    { label: 'Replace commercial lease', value: 'Replace commercial lease' },
  ];

  const content = {
    buttonControls: { cancel: false, submit: false },
    gap: '50px',
    sections: [
      {
        id: 'request-reason',
        label: 'Provide reason for request',
        ariaLabel: `Provide reason for request`,
        type: 'select',
        required: true,
        fields: [
          {
            id: 'provideReason',
            label: 'Provide reason for request',
            options: selectOptions,
            type: 'select',
            required: true,
            component: (props, formContext) => {
              // use this for a ref that the component has rendered for the first time
              // since we want to use the defaultStates that came when the page loaded,
              // avoiding a second network request to load the same data again

              return (
                <div
                  style={{ display: 'flex', gap: '12px', alignItems: 'center' }}
                >
                  {SelectControl(
                    { ...props, className: [] },
                    { id: props.id, options: selectOptions },
                  )}
                </div>
              );
            },
          },
        ],
      },

      {
        id: 'acknowledge',
        gap: '0px',
        sections: [
          {
            fields: [
              {
                label: 'Read and acknowledge statement below',
                type: 'readonly',
                required: true,
                element: {
                  control: {
                    modifier: () => null,
                  },

                  section: {
                    style: {
                      marginTop: '0rem',
                    },
                  },
                },
              },
            ],
          },
          {
            altStyle: 'alt-blue-no-hover',
            element: {
              section: {
                style: {
                  padding: 20,
                },
              },
            },
            fields: [
              {
                id: 'acknowledge',
                type: 'checkbox',
                label: (
                  <span className="acknowledge-section">
                    By requesting a leased vehicle through GSA Fleet, I
                    acknowledge that this is a{' '}
                    <strong>long-term vehicle need </strong> intended for
                    regular, ongoing operational use. For short-term vehicle
                    needs, I can utilize the{' '}
                    <strong>Short-Term Rental Program</strong> instead.
                  </span>
                ),
                element: {
                  label: {
                    style: {
                      maxWidth: '100%',
                    },
                  },
                },
              },
            ],
          },
        ],
      },
      {
        header: '',
        element: {
          header: {
            className: 'replace-vehicle-form-header',
          },
        },
        fields: [
          {
            label: 'Provide garage address',
            tooltip:
              'The garage address is the physical location of the vehicle. This address should align with its future CDD garage address, and will be used to determine a delivery location.',
          },
          {
            id: 'countryCode',
            label: 'Country',
            required: 'true',
            type: 'combobox',
            options: countries.data,
          },
          {
            id: 'addressLine1',
            label: 'Address line 1',
            type: 'text',
          },
          {
            id: 'addressLine2',
            label: 'Address line 2',
            type: 'text',
          },
          {
            id: 'city',
            label: 'City',
            type: 'text',
          },
          {
            id: 'stateCode',
            label: 'State',
            options: defaultStates.data,
            type: 'select',
            required: true,
            component: (props, formContext) => {
              const [states, setStates] = useState(defaultStates.data);
              // use this for a ref that the component has rendered for the first time
              // since we want to use the defaultStates that came when the page loaded,
              // avoiding a second network request to load the same data again
              const first = useRef(false);

              const watchCountry = formContext?.watch('countryCode');

              useEffect(() => {
                if (!first.current) {
                  first.current = true;
                  return;
                }

                getStates({
                  variables: { category: `ISOStateCode-${watchCountry}` },
                }).then((countryStates) => {
                  setStates(countryStates.data);
                  formContext?.setValue('stateCode', '-1');
                });
              }, [watchCountry]);

              return (
                <div
                  style={{ display: 'flex', gap: '12px', alignItems: 'center' }}
                >
                  {SelectControl(
                    { ...props, className: [], disabled: statesLoading },
                    { id: props.id, options: states },
                  )}
                  {statesLoading && <Spinner size="small" />}
                </div>
              );
            },
          },
          {
            id: 'zipCode',
            label: 'Postal code',
            type: 'text',
            required: true,
            onFormChange: (values) => {
              const countryCode = values.countryCode;
              if (
                countryCode !== 'Select Country' &&
                isInternationalCountry(countryCode)
              ) {
                return {
                  columnWidth: 'col-4-12',
                  label: 'Postal code',
                  required: false,
                };
              } else {
                return {
                  columnWidth: 'col-3-12',
                  label: 'Postal code',
                  required: true,
                };
              }
            },
            columnWidth: 'col-2-12',
          },
        ],
      },
      {
        gap: '32px',
        sections: [
          {
            fields: [
              {
                id: 'usedForTowing',
                label: 'Will the additional vehicle be used for towing?',
                type: 'radio',
                options: [
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ],
                required: true,
                element: {
                  control: {
                    inline: true,
                  },
                },
              },
            ],
          },
          {
            dependentOn: 'usedForTowing',
            dependentValue: 'yes',
            altStyle: 'alt-blue-no-hover',
            columnWidth: 'col-4-12',
            fields: [
              {
                label:
                  'Provide maximum combined towing weight of the trailer and load',
                required: true,
              },
              {
                fieldLayout: 'horizontal',
                columnWidth: 'col-9-12',
                fields: [
                  {
                    id: 'towingWeight',
                    type: 'number',
                    columnWidth: 'col-9-12',
                    element: {
                      control: {
                        style: { maxWidth: '100%' },
                      },
                    },
                  },
                  {
                    id: 'towingWeightUnit',
                    type: 'select',
                    columnWidth: 'col-3-12',
                    options: [
                      { label: 'lbs', value: 'lbs' },
                      { label: 'kg', value: 'kg' },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        header: 'Select Standard Item Number',
        subheader:
          'In accordance with FMR 102-34.50, vehicle selections should be limited to the minimum vehicle and engine size that is mission essential, and achieve maximum fuel efficiency',
        element: {
          header: {
            className: 'select-sin-header',
          },
          subheader: {
            style: { maxWidth: '1021px', marginBottom: '0px' },
          },
        },
      },
      {
        component: () => {
          return (
            sinAlert && (
              <Alert type="error">Please select vehicle type to proceed</Alert>
            )
          );
        },
      },
    ],
  };

  return {
    values,
    content,
  };
}
